import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Flame, Trophy, MapPin, Users, Sparkles, Pencil, Check, X, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SecretDashboard — Secret Admin overview.
 * Two sub-tabs: Top Players (leaderboard) and Tournaments (manage + feature).
 * Both share the same filter input.
 */
export default function SecretDashboard({ apiCall }) {
  const [data, setData] = useState({ locations: [], players: [], tournaments: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [tab, setTab] = useState('players'); // 'players' | 'tournaments'
  const [pinning, setPinning] = useState(null); // tournament_id mid-toggle
  const [editingId, setEditingId] = useState(null); // inline-edit state
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const d = await apiCall('/api/admin-secret/dashboard');
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Failed to load dashboard');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refresh();
      setLoading(false);
    })();
  }, []);

  /**
   * Toggle the secret-admin `featured` flag. The backend enforces a
   * single-feature invariant (auto-unfeatures every other tournament when
   * turning ON), so we re-fetch the dashboard to mirror server truth.
   */
  const handleToggleFeatured = async (tournamentId, desired) => {
    setPinning(tournamentId);
    try {
      await apiCall(`/api/admin-secret/tournaments/${tournamentId}/featured`, {
        method: 'PUT',
        body: JSON.stringify({ featured: desired }),
      });
      // Optimistic local update first (instant feedback)
      setData(prev => ({
        ...prev,
        tournaments: prev.tournaments.map(t =>
          t.tournament_id === tournamentId
            ? { ...t, featured: desired }
            : desired ? { ...t, featured: false } : t
        ),
      }));
      toast.success(desired ? 'Pinned to landing page' : 'Removed from landing');
      // Then re-sync from the server to catch any drift
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Could not update');
    } finally {
      setPinning(null);
    }
  };

  const handleClearAllPins = async () => {
    if (!window.confirm('Clear the "featured" flag on every tournament?')) return;
    try {
      const res = await apiCall('/api/admin-secret/tournaments/unfeature-all', { method: 'POST' });
      toast.success(`Cleared ${res.cleared} pinned tournaments`);
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Could not clear');
    }
  };

  const startEdit = (t) => {
    setEditingId(t.tournament_id);
    setEditForm({
      name: t.name || '',
      max_participants: t.max_participants || t.team_count || 8,
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = async (tournamentId) => {
    const payload = {};
    if (editForm.name) payload.name = editForm.name;
    if (editForm.max_participants) payload.max_participants = Number(editForm.max_participants);
    if (Object.keys(payload).length === 0) { cancelEdit(); return; }
    setSaving(true);
    try {
      await apiCall(`/api/admin-secret/tournaments/${tournamentId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      toast.success('Tournament updated');
      cancelEdit();
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  // ── Derived data
  const ql = filter.trim().toLowerCase();
  const players = useMemo(() => {
    if (!ql) return data.players;
    return data.players.filter(p =>
      (p.name || '').toLowerCase().includes(ql) ||
      (p.city || '').toLowerCase().includes(ql)
    );
  }, [data.players, ql]);

  const tournaments = useMemo(() => {
    if (!ql) return data.tournaments;
    return data.tournaments.filter(t =>
      (t.name || '').toLowerCase().includes(ql) ||
      (t.city || '').toLowerCase().includes(ql) ||
      (t.venue || '').toLowerCase().includes(ql) ||
      (t.organizer_name || '').toLowerCase().includes(ql)
    );
  }, [data.tournaments, ql]);

  const pinnedCount = useMemo(
    () => data.tournaments.filter(t => t.featured).length,
    [data.tournaments]
  );

  if (loading) {
    return <p className="text-sm text-muted-foreground" data-testid="secret-dashboard-loading">Loading the realm…</p>;
  }

  return (
    <section data-testid="secret-dashboard-section" className="space-y-5">
      <Input
        placeholder="🔍  Filter by name, city, venue, organizer…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="max-w-md"
        data-testid="secret-dashboard-filter"
      />

      {/* Sub-tabs */}
      <div className="inline-flex rounded-lg border bg-card p-0.5" role="tablist" data-testid="dashboard-subtabs">
        <button
          type="button"
          onClick={() => setTab('players')}
          className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded transition-colors ${tab === 'players' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="subtab-players"
        >
          <Flame className="h-3.5 w-3.5 inline mr-1" /> Top Players ({players.length})
        </button>
        <button
          type="button"
          onClick={() => setTab('tournaments')}
          className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded transition-colors ${tab === 'tournaments' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          data-testid="subtab-tournaments"
        >
          <Trophy className="h-3.5 w-3.5 inline mr-1" /> Tournaments ({tournaments.length})
        </button>
      </div>

      {/* TOP PLAYERS TAB */}
      {tab === 'players' && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="dashboard-players-table">
              <thead className="bg-muted/30">
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Player</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">City</th>
                  <th className="text-right px-3 py-2 hidden sm:table-cell">Matches</th>
                  <th className="text-right px-3 py-2">W-L</th>
                  <th className="text-right px-3 py-2"><Flame className="h-3 w-3 inline" /> Streak</th>
                  <th className="text-right px-3 py-2">ELO</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p, i) => (
                  <tr key={`${p.shadow_id || 'no-sid'}-${i}`} className="border-t border-border" data-testid={`dashboard-player-row-${i}`}>
                    <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{p.name}</td>
                    <td className="px-3 py-2 hidden sm:table-cell text-muted-foreground">{p.city || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono hidden sm:table-cell">{p.matches}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.wins}-{p.losses}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.streak_weeks ? `${p.streak_weeks}🔥` : '—'}</td>
                    <td className="px-3 py-2 text-right font-mono">{p.elo}</td>
                  </tr>
                ))}
                {players.length === 0 && (
                  <tr><td colSpan={7} className="text-center px-3 py-6 text-muted-foreground">No matching players</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* TOURNAMENTS TAB */}
      {tab === 'tournaments' && (
        <div className="space-y-3">
          {/* Multiple-pins rescue */}
          {pinnedCount > 1 && (
            <Card className="border-destructive/40 bg-destructive/5" data-testid="multiple-pins-alert">
              <CardContent className="p-3 flex items-center gap-3">
                <ShieldAlert className="h-5 w-5 text-destructive flex-shrink-0" />
                <div className="flex-1 text-sm">
                  <strong>{pinnedCount} tournaments are pinned at once.</strong>
                  <span className="text-muted-foreground"> Only one should be featured at a time. Clear them all and pin a fresh one.</span>
                </div>
                <Button variant="destructive" size="sm" onClick={handleClearAllPins} data-testid="clear-all-pins-btn">
                  Clear all pins
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="grid sm:grid-cols-2 gap-2">
            {tournaments.map((t) => {
              const isEditing = editingId === t.tournament_id;
              return (
                <Card
                  key={t.tournament_id}
                  data-testid={`dashboard-tournament-${t.tournament_id}`}
                  className={t.featured ? 'border-amber-400/60 ring-1 ring-amber-400/40' : ''}
                >
                  <CardContent className="p-3">
                    {/* Header / name */}
                    <div className="flex items-start justify-between gap-2">
                      {isEditing ? (
                        <input
                          value={editForm.name}
                          onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                          className="font-bold text-sm flex-1 px-2 py-1 rounded border bg-background"
                          data-testid={`edit-name-${t.tournament_id}`}
                        />
                      ) : (
                        <a
                          href={`/tournaments/${t.tournament_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-sm hover:text-primary truncate flex-1"
                        >
                          {t.featured && <Sparkles className="h-3 w-3 inline-block mr-1 text-amber-400 align-[-1px]" />}
                          {t.name || '(unnamed)'}
                        </a>
                      )}
                      <Badge variant={t.status === 'in_progress' ? 'default' : 'outline'} className="text-[9px] uppercase">
                        {t.status?.replace('_', ' ') || '—'}
                      </Badge>
                    </div>

                    {/* Meta line */}
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-1 flex-wrap">
                      <span><Trophy className="h-3 w-3 inline" /> {t.format?.replace('_', ' ')}</span>
                      <span>
                        <Users className="h-3 w-3 inline" /> {t.team_count}
                        {isEditing ? (
                          <span className="ml-1">
                            / max <input
                              type="number"
                              min="2"
                              max="256"
                              value={editForm.max_participants}
                              onChange={(e) => setEditForm(f => ({ ...f, max_participants: e.target.value }))}
                              className="w-12 ml-1 px-1.5 py-0.5 rounded border bg-background text-foreground text-[11px]"
                              data-testid={`edit-max-${t.tournament_id}`}
                            />
                          </span>
                        ) : t.max_participants ? (
                          <span> / max {t.max_participants}</span>
                        ) : null}
                      </span>
                      {t.city && <span><MapPin className="h-3 w-3 inline" /> {t.city}</span>}
                      {t.organizer_name && <span className="truncate">by {t.organizer_name}</span>}
                    </div>

                    {/* Action bar */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-border gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">
                          {t.featured ? 'On landing page' : 'Feature on landing'}
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!t.featured}
                          disabled={pinning === t.tournament_id}
                          onClick={() => handleToggleFeatured(t.tournament_id, !t.featured)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${t.featured ? 'bg-amber-500' : 'bg-muted'}`}
                          data-testid={`feature-toggle-${t.tournament_id}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${t.featured ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </div>
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" onClick={cancelEdit} disabled={saving} data-testid={`cancel-edit-${t.tournament_id}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" onClick={() => saveEdit(t.tournament_id)} disabled={saving} data-testid={`save-edit-${t.tournament_id}`}>
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <Button size="icon" variant="ghost" onClick={() => startEdit(t)} data-testid={`edit-tournament-${t.tournament_id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {tournaments.length === 0 && (
              <p className="col-span-2 text-sm text-muted-foreground">No tournaments match.</p>
            )}
          </div>
        </div>
      )}

      {data.locations.length > 0 && (
        <p className="text-[10px] text-muted-foreground">
          Locations seen: {data.locations.slice(0, 8).join(' · ')}{data.locations.length > 8 ? ' …' : ''}
        </p>
      )}
    </section>
  );
}
